/* @flow */
import { getDefaultTimespanBackward } from '../util/date_utils'
import { calculateHorizon } from '../util/horizon_utils'
import {
    bucketTags, bucketTeams, bucketUsers,
    bucketTimeEntries, bucketTagReports,
    bucketGoals, ensureDateIfString, parseStringToEpochTime,
} from '../util/redux_utils'

export type Email = {
    address: string,
    verified: boolean,
}

export const EmailRecord: Email = {
    address: '',
    verified: false,
}

export type Profile = {
    email: Email,
    id: string,
    personalTeamId: string,
    roles: Array<string>,
    memberships: Map<string, TeamMember>,
    teamMembers:  Map<string, TeamMember>,
    fullName: string,
    accountId: string,
    onboardingStep: string,
    timezone: string,
    notificationPreferences: {
        weeklyReports: boolean,
        reminders: boolean,
        announcements: boolean,
        education: boolean,
    },
}

export const ProfileRecord = {
    email: EmailRecord,
    id: '',
    personalTeamId: '',
    role: [],
    memberships: {},
    fullName: '',
    accountId: '',
    onboardingStep: '',
    timezone: '',
    notificationPreferences: {
        weeklyReports: false,
        reminders: false,
        announcements: false,
        education: false,
    },
}

export type User = {
    id: string,
    primaryEmail: string,
    fullName: string,
    personalTeamId: string,
}

export const UserRecord = {
    id: '',
    primaryEmail: "",
    fullName: '',
    personalTeamId: '',
}

export type Span = {
    start: ?Date,
    end: ?Date,
}

export type Range = {
    increment: string,
    interval: string,
    subInterval: string,
    start: Date,
    end: Date,
}

const RangeRecord = {
    increment: 'Day',
    interval: 'Day',
    subInterval: 'Day',
    start: getDefaultTimespanBackward('Day').start,
    end: getDefaultTimespanBackward('Day').end,
}

export type ViewPort = {
    teamId: string,
    range: RangeRecord,
    horizon: Span,
    display: string,
    collapse: boolean,
    groupBy: string,
}

export type Team = {
    id: string,
    ownerId: string,
    name: string,
    description: string,
    shortname: string,
    color: string,
    createdAt: ?Date,
    updatedAt: ?Date,
    isPersonal: boolean,
    isUpgraded: boolean,
    teamOwnerName: string,
    teamOwnerEmail: string,
    trialEnd: null,
}

export type Teams = {
    items: Map<string, Team>,
    sort: Array<string>,
}

export const TeamsRecord ={
    items: {},
    sort: [],
}

export type Users = {
    items: Map<string, Profile>,
    sort: Array<string>,
}

export const UsersRecord = {
    items: {},
    sort: [],
}

export type TeamMember = {
    id: string,
    teamId: string,
    accessLevel: string,
    primaryEmail: string,
    name: Name,
    isActive: boolean, 
    userId: string,
    // isActiveUser: boolean // not currently being used
}

export const TeamMemberRecord = {
    id: '',
    teamId: '',
    accessLevel: '',
    primaryEmail: '',
    userId: '',
}

export type TeamMembership = {
    teamId: string,
    items: Map<string, TeamMember>,
    sort: Array<string>,
}

export const TeamMembershipRecord = {
    teamId: '',
    items: {},
    sort: [],
}

export type TeamInvite = {
    id: string,
    teamId: string,
    inviterId: string,
    inviterFullName?: string, 
    inviterEmail?: string,
    teamName?: string,
    invitedId: string,
    invitedEmail: string,
    invitedFullName: string,
    createdAt: ?Date,
    updatedAt: ?Date,
    isAccepted: boolean,
}

export const TeamInviteRecord = {
    id: '',
    teamId: '',
    inviterId: '',
    invitedId: '',
    invitedEmail: '',
    invitedFullName: '',
    createdAt: null,
    updatedAt: null,
    isAccepted: false,
}

export type TeamInvites = {
    teamId: string,
    items: Map<string, TeamInvite>,
    sort: Array<string>,
}

export const TeamInvitesRecord = {
    teamId: '',
    items: {},
    sort: [],
}

export type Tag = {
    id: string,
    userId: string,
    teamId: string,
    name: string,
    color: string,
    isArchived: boolean,
}

export type Tags = {
    items: Map<string, Tag>,
    sort: Array<string>,
}

export const TagsRecord = {
    items: {},
    sort: [],
}

export type TagReports = {
    items: Map<string, TagReport>,
    sort: Array<string>,
}

export const TagReportsRecord = {
    items:{},
    sort: [],
}

export type TimeEntry = {
    id: string,
    userId: string,
    startAt: ?Date,
    endAt: ?Date,
    duration: ?number,
    description: ?string,
    detail: ?string,
    tags: Array<string>,
    // TODO: Multiteam
    teamId: string,
    teams: Array<string>,
    createdAt: ?Date,
    updatedAt: ?Date,
}

export type TimeEntries = {
    items: Map<string, TimeEntry>,
    sort: Array<string>,
}

export const TimeEntriesRecord = {
    items: {},
    sort: [],
}

export type RunningTimeEntryIdMap = Map<string, Array<string>>

export type TagReportDetail = {
    ownerId: string,
    totalDuration: number,
}

export type TagReport = {
    id: string,
    tag: string,
    totalDuration: number,
    detail: Array<TagReportDetail>,
}

export type RecentTags = Array<TagReport>

export type TagCombo = {
    id: string,
    tags: Array<string>,
}

export const TagComboRecord = {
    id: '',
    tags: [],
}

export const ToTagComboRecord = (combo) => {
    return { ...combo, tags: [...combo.tags] }
}

export type TagCombos = Array<TagCombo>

export type GoalProgress = {
    id: string,
    totalDuration: number,
}

export type GoalTag = {
    tagId: string,
}

export type GoalUser = {
    userId: string,
}

export type Goal = {
    id: string,
    teamId: string,
    name: string,
    tags: Array<GoalTag>,
    users: Array<GoalUser>,
    visibility: string,
    interval: string,
    budgetDuration: number,
    progress: GoalProgress,
    startDate: number,
    endDate: number,
    createdAt: number,
    updatedAt: number,
}

export type Goals = {
    items: Map<string, Goal>,
    sort: Array<string>,
}

export const GoalsRecord = {
    items: {},
    sort: [],
}

export type Ui = {
    pwModalOpen: boolean,
    paymentModalOpen: boolean,
    selectedSidebarTab: string,
    isLoading: boolean,
    selectedAdminTab: string,
    selectedSettingsTab: String,
    clearSettingsPassword: Boolean,
}

export const UiRecord = {
    pwModalOpen: false,
    paymentModalOpen: false,
    selectedSidebarTab: 'profile',
    isLoading: false,
    selectedAdminTab: 'entries',
    selectedSettingsTab: 'profile',
    clearSettingsPassword: false,
}

export type Payment = {
    id: string,
    stripeChargeId: string,
    stripeInvoiceId: string,
    stripeInvoiceNumber: string,
    paymentMethod: string,
    customerId: string,
    amount: number,
    createdAt: number,
    updatedAt: number,
}

export type Customer = {
    id: string,
    ccBrand: string,
    ccExpDate: string,
    ccLast4: string,
    createdAt: number,
    paymentStatus: string,
    stripePlan: string,
    updatedAt: number,
    trialEnd: number,
    payments: Array<Payment>,
}

export const CustomerRecord = {
    id: '',
    ccBrand: '',
    ccExpDate: '',
    ccLast_4: '',
    createdAt: null,
    paymentStatus: '',
    stripePlan: '',
    updatedAt: null,
    trialEnd: null,
    payments: [],
}

export type Account = {
    customer: Customer,
    createdAt: String,
    customerId: String,
    id: String,
    name: String,
    ownerId: String,
    updatedAt: String,
    usersCount: number,
    users: string,
    tier: string, 
    cycle: string,
    alwaysFree: boolean,
}

export type Accounts = {
    items: Map<string, Account>,
    sort: Array<string>,
}

export const AccountsRecord = {
    items: {},
    sort: [],
}

const defaultDashSpan = getDefaultTimespanBackward('Day')

type AppState = {
    viewPort: ViewPort,
    tags: Tags,
    goals: Goals,
    users: Users,
    teams: Teams,
    timeEntries: TimeEntries,
    recentTagCombos: Array<string>,
    recentTags:  Array<string>,
    runningTimeEntries: Map<string, Array>,
    bootstrapped: boolean,
    profile: Profile,
    selectedTeamMembership: TeamMembership,
    selectedTeamInvites: TeamInvites,
    tagReports: TagReports,
    ui: Ui,
    customer: Customer,
    accounts: Accounts,
    currentInvite : TeamInvite | null,
}

export const AppStateRecord = {
    viewport: {
        teamId: '',
        collapse: false,
        display: 'list',
        groupBy: 'day',
        horizon: calculateHorizon(defaultDashSpan, 20),
        range: RangeRecord,
    },
    tags: TagsRecord,
    goals: GoalsRecord,
    users: UsersRecord,
    teams: TeamsRecord,
    timeEntries: TimeEntriesRecord,
    recentTagCombos: [],
    recentTags: [],
    runningTimeEntries: {},
    bootstrapped: false,
    profile: ProfileRecord,
    selectedTeamMembership: TeamMembershipRecord,
    selectedTeamInvites: TeamInvitesRecord,
    tagReports: TagReportsRecord,
    ui: UiRecord,
    customer: CustomerRecord,
    accounts: AccountsRecord,
    currentInvite: null,
}

export const INITIAL_STATE: AppState = AppStateRecord

// core functions

// viewport

export function setRange(state: ViewPort, range): Range {
    return {...state, range}
}

export function setHorizon(state: ViewPort, horizon: Span): ViewPort {
    return {...state, horizon}
}

export function setCollapse(state: ViewPort, collapse: boolean): ViewPort {
    return {...state, collapse}
}

export function setDisplay(state: ViewPort, display: string): ViewPort {
    const newState: ViewPort = {...state, display}
    return newState
}

export function setGroupBy(state: ViewPort, groupBy: string): ViewPort {
    return {...state, groupBy}
}

// tags

export function setTags(state: Tags, tags: Array<Tag>): Tags {
    return bucketTags(tags)
}

export function addTag(state: Tags, tag: Tag): Tags {
    // set the tag keyed by id
    const { items, sort } = state
    const newItems = {...items, [tag.id]: tag }
    const newSort = () => {
        const prevIndex = sort.indexOf(tag.id)
        // if the tag aleady exists, don't add it and return old sort
        if (prevIndex !== -1) {
            return sort
        }
        const alphabeticalByNameSort = [tag.id, ...sort]
            .map(id => newItems[id])
            .sort(function(a,b) {
                var textA = a.name.toUpperCase()
                var textB = b.name.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
            .map(object => object.id)
        return alphabeticalByNameSort
    }
    return {items: newItems, sort: newSort()}
}

export function setRecentTagCombos(state: TagCombos, recentTagCombos: TagCombos): TagCombos {
    return recentTagCombos.map(ToTagComboRecord)
}

export function addRecentTagCombo(state: TagCombos, tagCombo: TagCombo): TagCombos {
    const record = { id: tagCombo.id, tags: tagCombo.tags }
    const recordIndex = state.findIndex(combo => {
        const sameLength = combo.tags.size === record.tags.size
        const tagsMatch  = combo.tags.every(tagId => record.tags.findIndex(id => id === tagId) !== -1)
        return sameLength && tagsMatch
    })

    // if the combo already exists, just move it to the front
    if (recordIndex !== -1) {
        state.splice(recordIndex, 1)
        const newWithRecord = [record, ...state]
        return newWithRecord
    }
    // snip recent tag combos off after length of 5
    return [record, ...state].reduce((acc,combo) => {
        return acc.lengh > 5
            ? acc
            : [...acc, combo]
    }, [])
}

export function setRecentTags(state: RecentTags, tagReports: Array<TagReport>): RecentTags {
    return [...tagReports.map(tagReport => tagReport)]
}

export function deleteTag(state: Tags, tag: Object): AppState {
    const { sort, items } = state
    const newSort = sort.reduce((acc, itemId) => {
        return itemId === tag.id
            ? acc
            : [...acc, itemId]
    }, [])

    const newItems = sort.reduce((acc, itemId) => {
        return itemId === tag.id
            ? acc
            : {...acc, [itemId]: items[itemId]}
    }, {})
    return {items: newItems, sort: newSort}
}

export function deleteTagByTeamId(state: Tags, teamId: string): AppState {
    const tags = state.items
    const tagIDs = state.sort

    const newTagItems = tagIDs.reduce( (acc, tagId) =>
        (tags[tagId].teamId === teamId ? [...acc] : [...acc, tags[tagId]]), {})

    return setTags(state, newTagItems)
}

// tag reports

export function setTagReports(tagReports: Array<TagReport>): TagReports {
    return bucketTagReports(tagReports)
}

export function clearTagReports(): TagReports {
    return  INITIAL_STATE.tagReports
}

// teams

export function setTeams(state: Teams, teams: Array<Team>): Teams {
    return bucketTeams(teams)
}

export function addTeam(state: Teams, team: Team): Teams {
    // set the team keyed by id
    const { items, sort } = state
    const newItems = {...items, [team.id]: team }
    const newSort = () => {
        const prevIndex = sort.indexOf(team.id)
        // if the team aleady exists, don't add it and return old sort
        if (prevIndex !== -1) {
            return sort
        }
        const alphabeticalByNameSort = [team.id, ...sort]
            .map(id => newItems[id])
            .sort(function(a,b) {
                var textA = a.name.toUpperCase()
                var textB = b.name.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
            .map(object => object.id)
        return alphabeticalByNameSort
    }
    return {items: newItems, sort: newSort()}
}

export function deleteTeam(state: Teams, teamId: string): Teams {
    const { items, sort } = state
    const newSort = sort.reduce((acc, id)=> {
        return id === teamId
            ? acc
            : [...acc, id]
    }, [])
    const newItems = newSort.reduce((acc, id )=> {
        return {acc, [id]: items[id]}
    }, {})
    return {newItems, newSort}
}

// users -- currently only used in users_reducer, which is not being used

export function setUsers(state: Users, users: Array<User>): Users {
    return bucketUsers(users)
}

export function addUser(state: Users, user: User): Users {

    const { items, sort } = state
    const newItems = {...items, [user.id]: user}
    const newSort = () => {
        const prevIndex = sort.indexOf(user.id)
        // if the user aleady exists, don't add it and return old sort
        if (prevIndex !== -1) {
            return sort
        }
        const alphabeticalByEmailSort = [user.id, ...sort]
            .map(id => newItems[id])
            .sort(function(a,b) {
                var textA = a.primaryEmail.toUpperCase()
                var textB = b.primaryEmail.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
            .map(object => object.id)
        return alphabeticalByEmailSort
    }
    return {items: newItems, sort: newSort()}
}

// team membership

export function setTeamMembership(state: TeamMembership, teamId: string, members: TeamMember[]): TeamMembership {
    const items = members.map((member) => {
        return {
            id: member.id,
            teamId: member.teamId,
            accessLevel: member.accessLevel,
            primaryEmail: member.primaryEmail,
            fullName: member.fullName || '',
            isActive: member.isActive,
        }
    }).reduce( (acc, member: TeamMember) => {
        return {...acc, [member.id]: member}
    }, {} )
    const sort = members.sort( (a, b) => a.primaryEmail === b.primaryEmail ? 0 : a.primaryEmail > b.primaryEmail ? 1 :  -1)
        .map( m => m.id )

    return {
        teamId: teamId,
        items: items,
        sort: sort,
    }
}

export function updateTeamMembership(state: TeamMembership, member: TeamMember): TeamMembership {
    if (state.teamId !== member.teamId) {
        return state
    }
    const {
        id,
        teamId,
        accessLevel,
        primaryEmail,
    } = member
    const fixMember = {
        id,
        teamId,
        accessLevel,
        primaryEmail,
    }
    const { items, sort } = state
    const newItems = {...items, [id]: fixMember }
    const newSort = () => {
        const prevIndex = sort.indexOf(id)
        if (prevIndex !== -1) {
            return sort
        }
        const alphabeticalByEmailSort = [member.id, ...sort]
            .map(id => newItems[id])
            .sort(function(a,b) {
                var textA = a.primaryEmail.toUpperCase()
                var textB = b.primaryEmail.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
            .map(object => object.id)
        return alphabeticalByEmailSort
    }
    return {...state, items: newItems, sort: newSort()}
}

export function deleteTeamMember(state: TeamMembers, teamMember: TeamMember): TeamMembers {
    const { sort, items } = state
    const newSort = sort.reduce((acc, itemId) => {
        return itemId === teamMember.id
            ? acc
            : [...acc, itemId]
    }, [])

    const newItems = sort.reduce((acc, itemId) => {
        return itemId === teamMember.id
            ? acc
            : {...acc, [itemId]: items[itemId]}
    }, {})
    return {items: newItems, sort: newSort}
}

// team membership

export function setTeamInvites(state: TeamInvites, teamId: string, invites: TeamInvite[]): TeamInvites {
    const items = invites.reduce( (acc, t: TeamInvite) => ({...acc, [t.id]: t}), {} )
    const sort = invites
        .sort( (a, b) => a.id === b.id ? 0 : a.id > b.id ? 1 :  -1)
        .map( m => m.id )

    return {
        teamId: teamId,
        items: items,
        sort: sort,
    }
}

export function updateTeamInvites(state: TeamInvites, invite: TeamInvite): TeamInvites {
    if (state.teamId !== invite.teamId) {
        return state
    }
    const { items, sort } = state

    const newItems = {...items, [invite.id]: invite }
    const newSort = () => {
        const prevIndex = sort.indexOf(invite.id)
        const arrWithoutId = prevIndex > -1 ? sort.splice(prevIndex, 1) : sort
        const lastIndex = arrWithoutId.findIndex(id => {
            const record = state.items[id]
            return record.inviteEmail.toLowerCase() > invite.inviteEmail.toLowerCase()
        })
        const index = lastIndex > -1 ? lastIndex : items.size
        return arrWithoutId.slice(index, invite.id)
    }
    return {items: newItems, sort: newSort()}
}

export function deleteTeamInvite(state: TeamInvites, teamInvite: TeamInvite): TeamInvites {
    const { sort, items } = state
    const newSort = sort.reduce((acc, itemId) => {
        return itemId === teamInvite.id
            ? acc
            : [...acc, itemId]
    }, [])

    const newItems = sort.reduce((acc, itemId) => {
        return itemId === teamInvite.id
            ? acc
            : {...acc, [itemId]: items[itemId]}
    }, {})
    return {items: newItems, sort: newSort}
}

// time entries

export function setTimeEntries(state: TimeEntries, timeEntries: Array<TimeEntry>): TimeEntries {
    const newTime = bucketTimeEntries(timeEntries)
    return newTime
}

export function mergeTimeEntries(state: TimeEntries, timeEntries: Array<TimeEntry>): TimeEntries {
    const toMerge = bucketTimeEntries(timeEntries)
    const mergedState = { ...state }

    for (var i = 0; i < toMerge.sort.length; i++) {
        const entryId = toMerge.sort[i]
        if (mergedState.sort.indexOf(entryId) < 0) {
            mergedState.sort.push(entryId)
            mergedState.items[entryId] = toMerge.items[entryId]
        }
    }

    return mergedState
}

export function addTimeEntry(state: TimeEntries, timeEntry: TimeEntry): TimeEntries {
    const { items, sort } = state

    // set the timeEntry keyed by id
    const newItems = {...items, [timeEntry.id]: timeEntry }
    const newSort = () => {
        if (sort.includes(timeEntry.id)) {
            return sort
        }
        const sortbyStart = [timeEntry.id, ...sort]
            .map(id => newItems[id])
            .sort(function(a,b) {
                var valueA = a.startAt
                var valueB = b.startAt
                return (valueA < valueB) ? -1 : (valueA > valueB) ? 1 : 0
            })
            .map(object => object.id)
        return sortbyStart
    }
    return {items: newItems, sort: newSort()}
}

export function deleteTimeEntry(state: TimeEntries, timeEntry: TimeEntry): TimeEntries {
    const { sort, items } = state
    const newSort = sort.reduce((acc, itemId) => {
        return itemId === timeEntry.id
            ? acc
            : [...acc, itemId]
    }, [])

    const newItems = sort.reduce((acc, itemId) => {
        return itemId === timeEntry.id
            ? acc
            : {...acc, [itemId]: items[itemId]}
    }, {})
    return {items: newItems, sort: newSort}
}
// running time entries

export function addRunningTimeEntryId(state: RunningTimeEntryIdMap, userId: string, id: string): RunningTimeEntryIdMap {
    // if the time entry doesn't belong to this user, don't add it to running time entries
    if (state[userId] === undefined) {
        return state
    }
    return {...state, [userId]: [...state[userId], id]}
}

export function setRunningTimeEntryIds(state: RunningTimeEntryIdMap, userId: string, ids: Array<string>): RunningTimeEntryIdMap {
    return { ...state, [userId]: [...ids] }
}

export function removeRunningTimeEntryId(state:Map<string, Array>, userId: string, id: string): Map<string, Array> {
    // if the time entry doesn't belong to this user, don't update running time entries

    if (state[userId] === undefined) {
        return state
    }
    const newRunning = [...state[userId].filter(entry => entry !== id)]
    return {...state, [userId]: newRunning}
}

// profile

export function setProfile(state: Profile, profilePerams) {
    const {
        id,
        memberships,
        personalTeamId,
        role,
        primaryEmail,
        verified,
        fullName,
        accountID,
        onboardingStep,
        timezone,
        weeklyReports,
        reminders,
        announcements,
        education,
        teamMembers,
    } = profilePerams
    const email: Email = {
        address: primaryEmail,
        verified: verified,
    }
    return {
        email,
        id,
        roles: role,
        personalTeamId,
        memberships,
        teamMembers,
        name: fullName,
        accountId: accountID,
        onboardingStep,
        timezone,
        notificationPreferences: {
            weeklyReports,
            reminders,
            announcements,
            education,
        },
    }
}

// customer

export function setCustomer(customerParams: Customer) {
    const { id,
        ccBrand,
        ccExpDate,
        ccLast_4,
        createdAt,
        paymentStatus,
        stripePlanId,
        updatedAt,
        trialEndTs,
        customerPayments } = customerParams

    return {
        id,
        ccBrand,
        ccExpDate,
        ccLast4: ccLast_4,
        createdAt: new Date(createdAt).getTime(),
        paymentStatus,
        stripePlan: stripePlanId,
        updatedAt: new Date(updatedAt).getTime(),
        trialEnd: parseStringToEpochTime(trialEndTs),
        payments: customerPayments,
    }
}

export function clearCustomer(): Customer {
    return INITIAL_STATE.customer
}

// account

export function setAccount(accountParams: Account) {
    const {
        createdAt,
        customerId,
        id,
        name,
        ownerId,
        updatedAt,
    } = accountParams

    return {
        createdAt: Date.parse(createdAt),
        customerId,
        id,
        name,
        ownerId,
        updatedAt: Date.parse(updatedAt),
    }
}



export function setAccounts(accounts: Account[]): Accounts {
    const fixedAccounts = Array.isArray(accounts) ? accounts : [accounts]

    const items = fixedAccounts
        .map(account => {
            return {
                id: account.id,
                ownerId: account.ownerId,
                updatedAt: ensureDateIfString(account.updatedAt),
                customerId: account.customerId,
                name: account.name,
                createdAt: ensureDateIfString(account.createdAt),
                usersCount: account.usersCount,
                users: account.users,
                tier: account.tier,
                cycle: account.cycle,
                alwaysFree: account.alwaysFree,
            }
        })
        .reduce((acc, account: Account) => {
            return { ...acc, [account.id]: account }
        }, {})

    const sort = fixedAccounts
        .slice()
        .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1))
        .map(account => account.id)

    return { items, sort }
}

// team dashboard

export function setTeamDashboardId(state: ViewPort, teamId: string): ViewPort {
    return {...state, teamId}
}

// goals

export function setGoals(state: Goals, goals: Array<Goal>): Goals {
    return bucketGoals(goals)
}

export function addGoal(state: Goals, goal: Goal): Goals {
    const { items, sort } = state
    // set the goal keyed by id
    const newItems = {...items, [goal.id]: goal }
    // insert the goal in alphabetical order
    const newSort = () => {
        const prevIndex = sort.indexOf(goal.id)
        // if the goal aleady exists, don't add it and return old sort
        if (prevIndex !== -1) {
            return sort
        }
        const alphabeticalByIDSort = [goal.id, ...sort]
            .map(id => newItems[id])
            .sort(function(a,b) {
                var textA = a.id.toUpperCase()
                var textB = b.id.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
            .map(object => object.id)
        return alphabeticalByIDSort
    }
    return {items: newItems, sort: newSort()}
}

export function deleteGoal(state: Goals, goal: Object): AppState {
    const { sort, items } = state
    const newSort = sort.reduce((acc, itemId) => {
        return itemId === goal.id
            ? acc
            : [...acc, itemId]
    }, [])

    const newItems = sort.reduce((acc, itemId) => {
        return itemId === goal.id
            ? acc
            : {...acc, [itemId]: items[itemId]}
    }, {})
    return {items: newItems, sort: newSort}
}

export function deleteGoalByTeamId(state: Goals, teamId: string): AppState {
    const goals = state.items
    const goalIDs = state.sort

    const newGoalItems = goalIDs.reduce( (acc, goalId) =>
        (goals[goalId].teamId === teamId ? [...acc] : [...acc, goals[goalId]]), {})

    return setGoals(state, newGoalItems)
}

export function updateUi(state: Ui, node: string, value: boolean): Ui {
    return {...state, [node]: value}
}



// WEBPACK FOOTER //
// ./src/redux/core.js